import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { ReviewDocHeader } from "components/icons/ReviewDocHeader";
import { SUBMITTED } from "constants/sensitiveData";
import { useModal } from "hooks/useModal";
import { DftFilingDraftCardBody } from "./DftFilingDraftCardBody";
import { ReviewDftDraftFiling } from "./ReviewDftDraftFiling";
import { ChatCartPaid } from "components/icons/Chat/ChatCartPaid";
import { useSubmitFilingDraftCardMutation } from "store/apis/filingDraftReview";
import { DftFilingDraftMetadata } from "types/Models/reviewDocumentAndBalancingPayment";

export type FilingDraftCardCustomData = {
  cta_data: null;
  card_status: string;
  metadata: DftFilingDraftMetadata;
};

export const DftFilingDraftCard = ({
  customData,
  groupId,
  size,
}: {
  customData: FilingDraftCardCustomData;
  groupId: string;
  size?: "regular" | "block";
}) => {
  const draftId = customData?.metadata?.dft_review_draft_id;
  const isSubmitted = customData.card_status === SUBMITTED;
  const reviewDftDraftModal = useModal();
  const [, { isLoading: isSubmitting, isSuccess }] =
    useSubmitFilingDraftCardMutation({
      fixedCacheKey: `submitDftFilingDraft_${draftId}`,
    });

  const getHeaderText = () => {
    if (isSubmitted) {
      return "Draft Reviewed";
    }

    return "Review Filing Draft";
  };

  const getHeaderIcon = () => {
    if (customData?.metadata?.is_approved) {
      return <ChatCartPaid />;
    }

    return <ReviewDocHeader />;
  };

  return (
    <div className="t-min-w-72 sm:t-min-w-0">
      <ChatCard.Root size={size}>
        <ChatCard.Header>
          <div className="t-gap-1.5 t-flex t-items-center">
            {getHeaderIcon()}
            {getHeaderText()}
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <DftFilingDraftCardBody
            approveStatus={customData?.metadata?.is_approved}
            rejectionReason={customData?.metadata?.rejection_reason || ""}
            taxAmountPayable={customData?.metadata?.tax_amount_payable || 0}
          />
          {isSubmitted ? (
            <Button size="small" onClick={reviewDftDraftModal.open}>
              View
            </Button>
          ) : (
            <Button
              size="small"
              customType="primary"
              onClick={reviewDftDraftModal.open}
              isLoading={isSubmitting || isSuccess}
            >
              Review
            </Button>
          )}
        </ChatCard.Body>
        {reviewDftDraftModal.isOpen && (
          <ReviewDftDraftFiling
            {...reviewDftDraftModal}
            draftId={draftId}
            groupId={groupId}
            isSubmitted={isSubmitted}
            rejectionReason={customData?.metadata?.rejection_reason || ""}
            approvalStatus={customData?.metadata?.is_approved}
            submittedBy={customData?.metadata?.submitted_by?.name || null}
            fileId={customData?.metadata?.file_id}
            taxAmount={customData?.metadata.tax_amount_payable}
            grossAssetValue={customData?.metadata.gross_asset_value}
          />
        )}
      </ChatCard.Root>
    </div>
  );
};
